import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicShell from "@/layouts/PublicShell";
import StateBadge from "@/components/StateBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/format";
import {
  ShieldCheck,
  ShieldAlert,
  Fingerprint,
  Search,
  Tv,
  Film,
  Clock,
  Info,
} from "lucide-react";

/**
 * /verify — public Proof-of-Play verifier.
 * Anyone with a signed row (play_id + screen_id + media_id + timestamp + signature)
 * can independently confirm it was really delivered.
 */

const EMPTY = {
  play_id: "",
  screen_id: "",
  media_id: "",
  timestamp: "",
  signature: "",
};

export default function Verify() {
  const [sp] = useSearchParams();
  const [form, setForm] = useState({
    play_id: sp.get("play_id") || "",
    screen_id: sp.get("screen_id") || "",
    media_id: sp.get("media_id") || "",
    timestamp: sp.get("timestamp") || "",
    signature: sp.get("signature") || "",
  });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [rawText, setRawText] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const parsePaste = () => {
    // Try JSON first
    try {
      const j = JSON.parse(rawText);
      setForm({
        play_id: j.play_id || j.id || "",
        screen_id: j.screen_id || "",
        media_id: j.media_id || "",
        timestamp: j.timestamp || "",
        signature: j.signature || "",
      });
      return;
    } catch (_) {}
    // Try CSV row: play_id,timestamp,screen_id,media_id,signature
    const parts = rawText.split(",").map((p) => p.trim());
    if (parts.length >= 5) {
      setForm({
        play_id: parts[0],
        timestamp: parts[1],
        screen_id: parts[2],
        media_id: parts[3],
        signature: parts[4],
      });
    }
  };

  const verify = async () => {
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/proof-of-play/verify", form);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const clearAll = () => {
    setForm(EMPTY);
    setResult(null);
    setError("");
    setRawText("");
  };

  const valid = result?.valid === true;

  return (
    <PublicShell>
      <section className="pt-28 md:pt-32 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-14">
          <div className="flex items-center gap-3 mb-4">
            <StateBadge state="LIVE" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Public Proof-of-Play Verifier
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-[0.95] max-w-4xl">
            Verify a play.
            <br />
            <span className="text-white/50">No login. No trust.</span>
          </h1>
          <p className="text-white/60 mt-6 max-w-2xl">
            Every playback delivered on the Mirrorloops network is signed with{" "}
            <span className="font-mono text-[#E8D6A3]">HMAC-SHA256</span>. Paste
            any signed row below to independently confirm it really happened.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* LEFT: form */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3 flex items-center gap-2">
              <Fingerprint size={12} /> Signed row
            </div>

            <div className="mb-6">
              <Label className="text-xs uppercase tracking-[0.2em] text-white/40 mb-2 block">
                Paste JSON or CSV row
              </Label>
              <Textarea
                data-testid="verify-paste"
                value={rawText}
                onChange={(e) => setRawText(e.target.value)}
                placeholder='{"play_id":"…","screen_id":"…","media_id":"…","timestamp":"…","signature":"…"}'
                className="bg-white/[0.02] border-white/10 text-white/80 font-mono text-xs min-h-[80px]"
              />
              <div className="mt-2 flex gap-2">
                <Button
                  onClick={parsePaste}
                  data-testid="verify-parse"
                  variant="outline"
                  size="sm"
                  className="bg-white/[0.03] border-white/15 hover:border-white"
                >
                  Fill fields from paste
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              <FormField label="Play ID">
                <Input
                  data-testid="verify-play-id"
                  value={form.play_id}
                  onChange={set("play_id")}
                  placeholder="uuid…"
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
              </FormField>
              <FormField label="Screen ID">
                <Input
                  data-testid="verify-screen-id"
                  value={form.screen_id}
                  onChange={set("screen_id")}
                  placeholder="uuid…"
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
              </FormField>
              <FormField label="Media ID">
                <Input
                  data-testid="verify-media-id"
                  value={form.media_id}
                  onChange={set("media_id")}
                  placeholder="uuid…"
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
              </FormField>
              <FormField label="Timestamp (ISO 8601)">
                <Input
                  data-testid="verify-timestamp"
                  value={form.timestamp}
                  onChange={set("timestamp")}
                  placeholder="2026-02-19T10:15:00+00:00"
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
              </FormField>
              <FormField label="Signature (HMAC-SHA256[:32])">
                <Input
                  data-testid="verify-signature"
                  value={form.signature}
                  onChange={set("signature")}
                  placeholder="32-char hex…"
                  className="bg-white/[0.02] border-white/10 font-mono text-xs"
                />
              </FormField>
            </div>

            <div className="mt-6 flex gap-3">
              <Button
                onClick={verify}
                disabled={busy || !form.play_id || !form.signature}
                data-testid="verify-submit"
                className="bg-[#D4B878] hover:bg-[#E8D6A3] text-black rounded-full font-bold"
              >
                <Search size={14} className="mr-2" />
                {busy ? "Verifying…" : "Verify signature"}
              </Button>
              <Button
                onClick={clearAll}
                variant="outline"
                data-testid="verify-clear"
                className="bg-white/[0.03] border-white/15 hover:border-white"
              >
                Clear
              </Button>
            </div>

            {error && (
              <div
                data-testid="verify-error"
                className="mt-4 p-3 border border-red-500/30 bg-red-500/10 text-red-300 text-sm"
              >
                {error}
              </div>
            )}
          </div>

          {/* RIGHT: result */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
              Verification result
            </div>
            {!result && !error && (
              <div className="border border-white/10 p-8 text-white/40 text-sm bg-white/[0.02]">
                <Info size={16} className="mb-3 text-white/50" />
                Paste a signed row and press verify. The verifier recomputes the
                HMAC over the fields with the same server secret, and confirms
                the ledger still contains the record.
              </div>
            )}
            {result && (
              <div
                data-testid="verify-result"
                className={`border p-8 ${
                  valid
                    ? "border-emerald-500/40 bg-emerald-500/[0.05]"
                    : "border-red-500/40 bg-red-500/[0.05]"
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  {valid ? (
                    <ShieldCheck size={28} className="text-emerald-400" />
                  ) : (
                    <ShieldAlert size={28} className="text-red-400" />
                  )}
                  <div>
                    <div
                      data-testid="verify-status"
                      className={`text-2xl font-black tracking-tight ${
                        valid ? "text-emerald-300" : "text-red-300"
                      }`}
                    >
                      {valid ? "Verified" : "Not verified"}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                      {result.algorithm} · {result.signed_by}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-px bg-white/10 border border-white/10">
                  <ResultTile
                    ok={result.signature_match}
                    label="Signature match"
                    icon={<Fingerprint size={13} />}
                  />
                  <ResultTile
                    ok={result.exists_in_ledger}
                    label="In ledger"
                    icon={<Clock size={13} />}
                  />
                </div>

                {result.exists_in_ledger && (
                  <div className="mt-6 space-y-2 text-sm">
                    <RowLine
                      icon={<Tv size={13} />}
                      k="Screen"
                      v={result.screen_name || form.screen_id}
                    />
                    <RowLine
                      icon={<Film size={13} />}
                      k="Media"
                      v={result.media_name || form.media_id}
                    />
                    <RowLine
                      icon={<Clock size={13} />}
                      k="Played at"
                      v={new Date(form.timestamp).toLocaleString()}
                    />
                  </div>
                )}

                {!result.exists_in_ledger && result.signature_match && (
                  <div className="mt-6 text-xs text-white/50 border-l-2 border-white/20 pl-3">
                    Signature is mathematically valid, but the ledger row was
                    not found. This means the fields prove they were signed by
                    Mirrorloops, but the underlying record may have been
                    archived or the ID mistyped.
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 text-[11px] text-white/40 leading-relaxed max-w-md">
              How it works · Every play on the network is signed as{" "}
              <span className="font-mono text-white/70">
                HMAC(secret, play_id | screen_id | media_id | timestamp)
              </span>
              . The public verifier recomputes it deterministically. Anyone
              tampering with a field breaks the signature.
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-1 block">
        {label}
      </Label>
      {children}
    </div>
  );
}

function ResultTile({ ok, label, icon }) {
  return (
    <div className="p-5 bg-[#0A0908]">
      <div className={`mb-2 ${ok ? "text-emerald-400" : "text-red-400"}`}>
        {icon}
      </div>
      <div
        className={`text-lg font-black ${
          ok ? "text-emerald-300" : "text-red-300"
        }`}
      >
        {ok ? "PASS" : "FAIL"}
      </div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 mt-1">
        {label}
      </div>
    </div>
  );
}

function RowLine({ icon, k, v }) {
  return (
    <div className="flex items-center gap-3 border-b border-white/5 py-2">
      <span className="text-white/40">{icon}</span>
      <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 w-24">
        {k}
      </span>
      <span className="font-medium text-white/90 truncate">{v}</span>
    </div>
  );
}
